import React, { useCallback, useMemo, useState } from "react";
import API from "../../api/api";
import DataTable from "../../admin-panel/components/data/DataTable";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Grid,
  Typography,
  Box,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper
} from "@mui/material";

function TextInput({ label, value, onChange, placeholder, type = "text", style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, color: "#64748b" }}>{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type={type}
        style={{
          padding: "10px 12px",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          outline: "none",
          background: "#fff",
          ...style,
        }}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, color: "#64748b" }}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: "10px 12px",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          outline: "none",
          background: "#fff",
          ...style,
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Badge({ children, color = "#0369a1", bg = "#e0f2fe" }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1px 6px",
        lineHeight: 1,
        height: 18,
        fontSize: 10,
        borderRadius: 999,
        color,
        background: bg,
        fontWeight: 600,
      }}
    >
      {children}
    </span>
  );
}

const FRANCHISE_CATEGORY_OPTIONS = [
  { value: "", label: "All franchise levels" },
  { value: "agency_state_coordinator", label: "State Coordinator" },
  { value: "agency_state", label: "State" },
  { value: "agency_district_coordinator", label: "District Coordinator" },
  { value: "agency_district", label: "District" },
  { value: "agency_pincode_coordinator", label: "Pincode Coordinator" },
  { value: "agency_pincode", label: "Pincode" },
];

function categoryLabel(value) {
  const option = FRANCHISE_CATEGORY_OPTIONS.find((item) => item.value === value);
  return option?.label || value || "";
}

export default function AdminKYC({ audience = "consumer" }) {
  const isFranchise = audience === "franchise";
  // View mode bifurcation: 'kyc' vs 'nominee'
  const [viewMode, setViewMode] = useState("kyc"); // 'kyc' | 'nominee'
  const [selectedKycRow, setSelectedKycRow] = useState(null);
  const [editBankMode, setEditBankMode] = useState(false);
  const [editBankData, setEditBankData] = useState({ bank_name: "", bank_account_number: "", ifsc_code: "" });
  const [savingBank, setSavingBank] = useState(false);

  async function handleSaveBankEdit() {
    if (!selectedKycRow?.user_id) return;
    try {
      setSavingBank(true);
      await API.patch(`/admin/kyc/${selectedKycRow.user_id}/update/`, editBankData);
      setSelectedKycRow((prev) => ({
        ...prev,
        ...editBankData,
      }));
      setEditBankMode(false);
      setReloadKeyKyc((k) => k + 1);
      alert("Bank details updated successfully!");
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to update bank details");
    } finally {
      setSavingBank(false);
    }
  }

  // KYC filters
  const [kycFilters, setKycFilters] = useState({
    status: "pending", // default to pending approval queue
    user: "",
    state: "",
    pincode: "",
    category: "",
    date_from: "",
    date_to: "",
  });

  // Nominee filters (aligned to UserNominee fields where possible)
  const [nomineeFilters, setNomineeFilters] = useState({
    user: "",
    name: "",
    relationship: "",
    date_from: "",
    date_to: "",
    status: "", // e.g. "completed" | "incomplete" (backend optional)
  });

  const [density, setDensity] = useState("standard");
  const [reloadKeyKyc, setReloadKeyKyc] = useState(0);
  const [reloadKeyNominee, setReloadKeyNominee] = useState(0);

  function setKycF(key, val) {
    setKycFilters((f) => ({ ...f, [key]: val }));
  }
  function setNomineeF(key, val) {
    setNomineeFilters((f) => ({ ...f, [key]: val }));
  }

  const statusOptions = useMemo(
    () => [
      { value: "pending", label: "Pending Approval" },
      { value: "verified", label: "Verified" },
      { value: "", label: "All" },
    ],
    []
  );

  const nomineeRelationshipOptions = useMemo(
    () => [
      { value: "", label: "Any" },
      { value: "Spouse", label: "Spouse" },
      { value: "Parent", label: "Parent" },
      { value: "Child", label: "Child" },
      { value: "Sibling", label: "Sibling" },
      { value: "Other", label: "Other" },
    ],
    []
  );

  const nomineeStatusOptions = useMemo(
    () => [
      { value: "", label: "Any" },
      { value: "completed", label: "Completed" },
      { value: "incomplete", label: "Incomplete" },
    ],
    []
  );

  async function handleVerify(row) {
    if (!window.confirm(`Verify KYC for ${row.username}?`)) return;
    try {
      await API.patch(`/admin/kyc/${row.user_id}/verify/`);
      setReloadKeyKyc((k) => k + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to verify KYC");
    }
  }

  async function handleReject(row) {
    if (!window.confirm(`Reject KYC for ${row.username}?`)) return;
    try {
      await API.patch(`/admin/kyc/${row.user_id}/reject/`);
      setReloadKeyKyc((k) => k + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to reject KYC");
    }
  }

  // KYC DataGrid columns (responsive with flex + minWidth)
  const columnsKyc = useMemo(
    () => [
      { field: "user_id", headerName: "UserID", minWidth: 110 },
      { field: "username", headerName: "Username", minWidth: 160, flex: 1 },
      { field: "full_name", headerName: "Full Name", minWidth: 200, flex: 1 },
      { field: "phone", headerName: "Phone", minWidth: 140 },
      ...(isFranchise
        ? [
            {
              field: "category",
              headerName: "Franchise Layer",
              minWidth: 190,
              renderCell: (params) => categoryLabel(params?.row?.category),
              valueGetter: (_, row) => categoryLabel(row?.category),
            },
          ]
        : []),
      { field: "pincode", headerName: "Pincode", minWidth: 120 },
      {
        field: "bank",
        headerName: "Bank",
        minWidth: 200,
        renderCell: (params) => {
          const r = params?.row || {};
          return r.bank_name ? `${r.bank_name} (${r.ifsc_code})` : "";
        },
        valueGetter: (_, row) => {
          if (!row) return "";
          return row.bank_name ? `${row.bank_name} (${row.ifsc_code})` : "";
        },
      },
      {
        field: "bank_account_number",
        headerName: "Account No.",
        minWidth: 160,
        renderCell: (params) => {
          const v = params?.row?.bank_account_number || "";
          const s = String(v || "");
          if (!s) return "";
          if (s.length <= 4) return s;
          return s;
        },
        valueGetter: (_, row) => (row && row.bank_account_number) || "",
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 145,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const isVerified = Boolean(params?.row?.verified || params?.row?.status === "VERIFIED");
          const isRejected = params?.row?.status === "REJECTED";
          const hasBank = Boolean(
            (params?.row?.bank_name && String(params.row.bank_name).trim()) ||
            (params?.row?.bank_account_number && String(params.row.bank_account_number).trim()) ||
            (params?.row?.ifsc_code && String(params.row.ifsc_code).trim())
          );
          if (isVerified) {
            return <Badge color="#065f46" bg="#d1fae5">Verified</Badge>;
          } else if (isRejected) {
            return <Badge color="#991b1b" bg="#fee2e2">Rejected</Badge>;
          } else if (!hasBank || params?.row?.status === "NOT_SUBMITTED") {
            return <Badge color="#64748b" bg="#f1f5f9">Not Submitted</Badge>;
          } else {
            return <Badge color="#b45309" bg="#fef3c7">Pending Approval</Badge>;
          }
        },
        valueFormatter: (v) => v || "Pending",
      },
      {
        field: "__actions",
        headerName: "Actions",
        sortable: false,
        filterable: false,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const r = params?.row || {};
          const hasBank = Boolean(
            (r.bank_name && String(r.bank_name).trim()) ||
            (r.bank_account_number && String(r.bank_account_number).trim()) ||
            (r.ifsc_code && String(r.ifsc_code).trim())
          );
          return (
             <div style={{ display: "flex", gap: 8, width: "100%", height: "100%", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={() => { setSelectedKycRow(r); setEditBankMode(false); }}
                style={{
                  padding: "4px 8px",
                  background: "#3b82f6",
                  color: "#fff",
                  border: 0,
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Details
              </button>
              {!r.verified && hasBank ? (
                <button
                  onClick={() => handleVerify(r)}
                  style={{
                    padding: "4px 8px",
                    background: "#059669",
                    color: "#fff",
                    border: 0,
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  Verify
                </button>
              ) : null}
              <button
                onClick={() => handleReject(r)}
                style={{
                  padding: "4px 8px",
                  background: "#ef4444",
                  color: "#fff",
                  border: 0,
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Reject
              </button>
            </div>
          );
        },
      },
    ],
    [isFranchise]
  );

  // Nominee DataGrid columns (aligned with accounts.UserNomineeSerializer; admin endpoint may include user fields optionally)
  const columnsNominee = useMemo(
    () => [
      // Show user info if admin endpoint provides it; falls back to empty if not present
      { field: "user_id", headerName: "UserID", minWidth: 100 },
      { field: "username", headerName: "Username", minWidth: 160, flex: 1 },

      // Core nominee fields from accounts.UserNomineeSerializer
      { field: "name", headerName: "Nominee Name", minWidth: 200, flex: 1 },
      { field: "relationship", headerName: "Relationship", minWidth: 140 },
      { field: "phone", headerName: "Phone", minWidth: 140 },
      { field: "share_percent", headerName: "Share %", minWidth: 100, align: "right", headerAlign: "right",
        valueFormatter: (v) => (v == null || v === "" ? "" : `${v}%`) },

      // Timestamps
      { field: "updated_at", headerName: "Updated At", minWidth: 180 },
      { field: "created_at", headerName: "Created At", minWidth: 180 },

      // Optional status column if backend provides
      { field: "status", headerName: "Status", minWidth: 120, align: "center", headerAlign: "center",
        renderCell: (params) => {
          const status = String(params?.row?.status || "").toLowerCase();
          if (status === "completed") return <Badge color="#065f46" bg="#d1fae5">Completed</Badge>;
          if (status === "incomplete") return <Badge color="#92400e" bg="#fef3c7">Incomplete</Badge>;
          return String(params?.row?.status || "");
        },
      },
    ],
    []
  );

  // Server-side fetchers
  const fetcherKyc = useCallback(
    async ({ page, pageSize, search, ordering }) => {
      const params = { page, page_size: pageSize };
      params.audience = isFranchise ? "franchise" : "consumer";
      // Merge active filters (omit empty)
      Object.entries(kycFilters).forEach(([k, v]) => {
        if (v !== null && v !== undefined && String(v).trim() !== "") {
          params[k] = v;
        }
      });
      // Map quick search to backend-supported "user" filter
      if (search && String(search).trim()) {
        params.user = String(search).trim();
      }
      if (ordering) params.ordering = ordering;

      const res = await API.get("/admin/kyc/", { params });
      const data = res?.data;
      const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      const count = typeof data?.count === "number" ? data.count : results.length;
      return { results, count };
    },
    [isFranchise, kycFilters, reloadKeyKyc]
  );

  // Note: This expects an admin endpoint to list nominee records. If not present yet,
  // backend should implement GET /admin/nominees/ returning {count, results}.
  // We map search to both user and name so backend can choose which to honor.
  const fetcherNominee = useCallback(
    async ({ page, pageSize, search, ordering }) => {
      const params = { page, page_size: pageSize };
      Object.entries(nomineeFilters).forEach(([k, v]) => {
        if (v !== null && v !== undefined && String(v).trim() !== "") {
          params[k] = v;
        }
      });
      if (search && String(search).trim()) {
        params.user = String(search).trim();
        params.name = String(search).trim();
      }
      if (ordering) params.ordering = ordering;

      const res = await API.get("/admin/nominees/", { params });
      const data = res?.data;
      const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      const count = typeof data?.count === "number" ? data.count : results.length;
      return { results, count };
    },
    [nomineeFilters, reloadKeyNominee]
  );

  const toolbar = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <label style={{ fontSize: 12, color: "#64748b" }}>Density</label>
        <div style={{ display: "inline-flex", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
          <button
            onClick={() => setDensity("comfortable")}
            aria-pressed={density === "comfortable"}
            style={{
              padding: "6px 10px",
              fontSize: 12,
              background: density === "comfortable" ? "#0f172a" : "#fff",
              color: density === "comfortable" ? "#fff" : "#0f172a",
              border: 0,
              cursor: "pointer",
            }}
          >
            Comfortable
          </button>
          <button
            onClick={() => setDensity("standard")}
            aria-pressed={density === "standard"}
            style={{
              padding: "6px 10px",
              fontSize: 12,
              background: density === "standard" ? "#0f172a" : "#fff",
              color: density === "standard" ? "#fff" : "#0f172a",
              border: 0,
              borderLeft: "1px solid #e5e7eb",
              cursor: "pointer",
            }}
          >
            Standard
          </button>
          <button
            onClick={() => setDensity("compact")}
            aria-pressed={density === "compact"}
            style={{
              padding: "6px 10px",
              fontSize: 12,
              background: density === "compact" ? "#0f172a" : "#fff",
              color: density === "compact" ? "#fff" : "#0f172a",
              border: 0,
              borderLeft: "1px solid #e5e7eb",
              cursor: "pointer",
            }}
          >
            Compact
          </button>
        </div>
      </div>
      <button
        onClick={() => (viewMode === "kyc" ? setReloadKeyKyc((k) => k + 1) : setReloadKeyNominee((k) => k + 1))}
        style={{
          padding: "8px 12px",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          background: "#fff",
          color: "#0f172a",
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        Refresh
      </button>
    </div>
  );

  return (
    <div>
      {/* Page header + view toggle */}
      <div style={{ marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, color: "#0f172a" }}>{viewMode === "kyc" ? (isFranchise ? "Franchise KYC Verification" : "KYC Verification") : "Nominee Details"}</h2>
          <div style={{ color: "#64748b", fontSize: 13 }}>
            {viewMode === "kyc"
              ? isFranchise
                ? "Review only franchise hierarchy KYC records. Consumer KYC is excluded from this workspace."
                : "Review and decide consumer KYC. Use filters to find records quickly, and quick filter in the table toolbar."
              : "Review consumer nominee details. Use filters to find records quickly, and quick filter in the table toolbar."}
          </div>
        </div>
        <div
          role="tablist"
          aria-label="View mode"
          style={{ display: "inline-flex", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}
        >
          <button
            role="tab"
            aria-selected={viewMode === "kyc"}
            onClick={() => setViewMode("kyc")}
            style={{
              padding: "8px 14px",
              fontSize: 13,
              background: viewMode === "kyc" ? "#0f172a" : "#fff",
              color: viewMode === "kyc" ? "#fff" : "#0f172a",
              border: 0,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            KYC
          </button>
          <button
            role="tab"
            aria-selected={viewMode === "nominee"}
            onClick={() => setViewMode("nominee")}
            style={{
              padding: "8px 14px",
              fontSize: 13,
              background: viewMode === "nominee" ? "#0f172a" : "#fff",
              color: viewMode === "nominee" ? "#fff" : "#0f172a",
              border: 0,
              borderLeft: "1px solid #e5e7eb",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Nominee
          </button>
        </div>
      </div>

      {/* Filters */}
      {viewMode === "kyc" ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <Select
            label="Status"
            value={kycFilters.status}
            onChange={(v) => setKycF("status", v)}
            options={statusOptions}
          />
          <TextInput
            label="User"
            value={kycFilters.user}
            onChange={(v) => setKycF("user", v)}
            placeholder="user id / username / name / phone"
          />
          <TextInput
            label="State ID"
            value={kycFilters.state}
            onChange={(v) => setKycF("state", v)}
            placeholder="numeric state id"
          />
          <TextInput
            label="Pincode"
            value={kycFilters.pincode}
            onChange={(v) => setKycF("pincode", v)}
            placeholder="contains"
          />
          {isFranchise ? (
            <Select
              label="Franchise Layer"
              value={kycFilters.category}
              onChange={(v) => setKycF("category", v)}
              options={FRANCHISE_CATEGORY_OPTIONS}
            />
          ) : null}
          <TextInput
            label="Updated From"
            type="date"
            value={kycFilters.date_from}
            onChange={(v) => setKycF("date_from", v)}
            placeholder=""
          />
          <TextInput
            label="Updated To"
            type="date"
            value={kycFilters.date_to}
            onChange={(v) => setKycF("date_to", v)}
            placeholder=""
          />
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <TextInput
            label="User"
            value={nomineeFilters.user}
            onChange={(v) => setNomineeF("user", v)}
            placeholder="user id / username (if supported by backend)"
          />
          <TextInput
            label="Nominee Name"
            value={nomineeFilters.name}
            onChange={(v) => setNomineeF("name", v)}
            placeholder="full or partial name"
          />
          <Select
            label="Relationship"
            value={nomineeFilters.relationship}
            onChange={(v) => setNomineeF("relationship", v)}
            options={nomineeRelationshipOptions}
          />
          <Select
            label="Status"
            value={nomineeFilters.status}
            onChange={(v) => setNomineeF("status", v)}
            options={nomineeStatusOptions}
          />
          <TextInput
            label="Updated From"
            type="date"
            value={nomineeFilters.date_from}
            onChange={(v) => setNomineeF("date_from", v)}
            placeholder=""
          />
          <TextInput
            label="Updated To"
            type="date"
            value={nomineeFilters.date_to}
            onChange={(v) => setNomineeF("date_to", v)}
            placeholder=""
          />
        </div>
      )}

      {/* Table */}
      {viewMode === "kyc" ? (
        <DataTable
          columns={columnsKyc}
          fetcher={fetcherKyc}
          density={density}
          toolbar={toolbar}
          checkboxSelection={true}
          onSelectionChange={() => {}}
          instanceKey="kyc"
          extraKey={String(reloadKeyKyc)}
        />
      ) : (
        <DataTable
          columns={columnsNominee}
          fetcher={fetcherNominee}
          density={density}
          toolbar={toolbar}
          checkboxSelection={true}
          onSelectionChange={() => {}}
          instanceKey="nominee"
          extraKey={String(reloadKeyNominee)}
        />
      )}

      {/* Aadhaar Details Dialog */}
      {selectedKycRow && (
        <Dialog
          open={!!selectedKycRow}
          onClose={() => setSelectedKycRow(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>KYC Profile Details</span>
            {selectedKycRow?.verified || selectedKycRow?.status === "VERIFIED" ? (
              <Badge color="#065f46" bg="#d1fae5">Verified</Badge>
            ) : selectedKycRow?.status === "REJECTED" ? (
              <Badge color="#991b1b" bg="#fee2e2">Rejected</Badge>
            ) : (!selectedKycRow?.bank_name && !selectedKycRow?.bank_account_number && !selectedKycRow?.ifsc_code) || selectedKycRow?.status === "NOT_SUBMITTED" ? (
              <Badge color="#64748b" bg="#f1f5f9">Not Submitted</Badge>
            ) : (
              <Badge color="#b45309" bg="#fef3c7">Pending Approval</Badge>
            )}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3} sx={{ display: "flex", justifyContent: "center" }}>
                {selectedKycRow.photo ? (
                  <Avatar
                    src={`data:image/jpeg;base64,${selectedKycRow.photo}`}
                    variant="rounded"
                    sx={{ width: 110, height: 130, border: "2px solid #ccc" }}
                  />
                ) : (
                  <Avatar variant="rounded" sx={{ width: 110, height: 130, bgcolor: "#3b82f6" }}>
                    {selectedKycRow.kyc_name ? selectedKycRow.kyc_name.charAt(0) : "U"}
                  </Avatar>
                )}
              </Grid>
              <Grid item xs={12} sm={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Name (as per Aadhaar)</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{selectedKycRow.kyc_name || selectedKycRow.full_name || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">DOB</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{selectedKycRow.dob || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Gender</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{selectedKycRow.gender || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Aadhaar Last 4</Typography>
                    <Typography sx={{ fontWeight: 600 }}>xxxx-xxxx-{selectedKycRow.aadhaar_last4 || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">PAN Number</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{selectedKycRow.pan_number || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Mobile</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{selectedKycRow.kyc_mobile || selectedKycRow.phone || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{selectedKycRow.kyc_email || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Address</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedKycRow.kyc_address || "N/A"}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Bank Payout Details Section */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "text.secondary" }}>
                  Bank Payout Details
                </Typography>
                {selectedKycRow.verified && !editBankMode && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setEditBankData({
                        bank_name: selectedKycRow.bank_name || "",
                        bank_account_number: selectedKycRow.bank_account_number || "",
                        ifsc_code: selectedKycRow.ifsc_code || "",
                      });
                      setEditBankMode(true);
                    }}
                  >
                    Edit Bank Details
                  </Button>
                )}
              </Box>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: editBankMode ? "#f0fdf4" : "#fafafa", borderColor: editBankMode ? "#86efac" : undefined }}>
                {editBankMode ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Bank Name"
                        value={editBankData.bank_name}
                        onChange={(e) => setEditBankData((d) => ({ ...d, bank_name: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Account Number"
                        value={editBankData.bank_account_number}
                        onChange={(e) => setEditBankData((d) => ({ ...d, bank_account_number: e.target.value.replace(/[^0-9A-Za-z]/g, "") }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        label="IFSC Code"
                        value={editBankData.ifsc_code}
                        onChange={(e) => setEditBankData((d) => ({ ...d, ifsc_code: e.target.value.toUpperCase().slice(0, 11) }))}
                        inputProps={{ maxLength: 11 }}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                      <Button size="small" onClick={() => setEditBankMode(false)}>Cancel</Button>
                      <Button size="small" variant="contained" color="success" disabled={savingBank} onClick={handleSaveBankEdit}>
                        {savingBank ? "Saving..." : "Save Bank Updates"}
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Bank Name</Typography>
                      <Typography sx={{ fontWeight: 700 }}>{selectedKycRow.bank_name || "N/A"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Account Number</Typography>
                      <Typography sx={{ fontWeight: 700 }}>{selectedKycRow.bank_account_number || "N/A"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">IFSC Code</Typography>
                      <Typography sx={{ fontWeight: 700 }}>{selectedKycRow.ifsc_code || "N/A"}</Typography>
                    </Grid>
                  </Grid>
                )}
              </Paper>
            </Box>

            {selectedKycRow.issued_documents_json && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Linked Issued Documents</Typography>
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                  <List dense>
                    {(() => {
                      try {
                        const docs = JSON.parse(selectedKycRow.issued_documents_json);
                        const items = docs?.items || [];
                        if (items.length === 0) return <ListItem><ListItemText primary="No issued documents." /></ListItem>;
                        return items.map((doc, idx) => (
                          <ListItem key={idx} divider={idx < items.length - 1}>
                            <ListItemText
                              primary={doc.name}
                              secondary={`URI: ${doc.uri} | Type: ${doc.type || "Document"}`}
                              primaryTypographyProps={{ fontWeight: 700 }}
                            />
                          </ListItem>
                        ));
                      } catch {
                        return <ListItem><ListItemText primary="No documents." /></ListItem>;
                      }
                    })()}
                  </List>
                </Paper>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => { setSelectedKycRow(null); setEditBankMode(false); }} color="inherit">Close</Button>
            {!selectedKycRow.verified && (
              <>
                <Button
                  onClick={async () => {
                    if (window.confirm(`Reject KYC for ${selectedKycRow.username}?`)) {
                      await handleReject(selectedKycRow);
                      setSelectedKycRow(null);
                    }
                  }}
                  color="error"
                  variant="outlined"
                >
                  Reject
                </Button>
                <Button
                  onClick={async () => {
                    if (window.confirm(`Verify KYC for ${selectedKycRow.username}?`)) {
                      await handleVerify(selectedKycRow);
                      setSelectedKycRow(null);
                    }
                  }}
                  color="success"
                  variant="contained"
                >
                  Verify & Approve
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}
